import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef } from "react";

const noop = () => {};

type DebounceOptions = {
  leading?: boolean;
  maxWait?: number;
  trailing?: boolean;
};

type DebouncedFunc<T extends (...args: any[]) => any> = ((
  ...args: Parameters<T>
) => ReturnType<T> | undefined) & {
  cancel(): void;
  flush(): ReturnType<T> | undefined;
};

export const useDebounce = <T extends (...args: any[]) => any>(
  fn: T,
  wait = 100,
  options?: DebounceOptions
): DebouncedFunc<T> => {
  const fnRef = useRef<(...args: any[]) => any>(noop);

  useEffect(() => {
    fnRef.current = fn;
    return () => {
      fnRef.current = noop;
    };
  }, [fn]);

  return useMemo(
    () => debounce((...attrs) => fnRef.current(...attrs), wait, options) as any as DebouncedFunc<T>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wait, options?.leading, options?.maxWait, options?.trailing]
  );
};
